import React, { useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Typography,
  Chip,
  Stack,
  Alert,
  Switch,
  Tooltip,
  Box,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Button } from 'components/index';

export type DrawOption = { id: string; name: string; applicationNumber?: number; isEnabled?: boolean };

/** Gates whether a report can cover more than one draw (G702). Off: selection is forced single-draw. */
const MULTI_G702_ENABLED = process.env.REACT_APP_ENABLE_MULTI_G702 === 'true';

interface MagicReportDrawSelectDialogProps {
  open: boolean;
  onClose: () => void;
  draws: DrawOption[];
  projectName?: string;
  onConfirm: (drawIds: string[], hiddenCoLienDrawIds: string[]) => void;
  isLoading?: boolean;
  /** Draws already covered by the existing report; pre-checked so reopening keeps the same set. */
  initiallySelectedDrawIds?: string[];
  /**
   * Draws whose Change Orders / Lien Waivers sections the existing report hides. Pre-seeded so
   * that starting the next report without touching anything reproduces last month's layout.
   */
  initiallyHiddenCoLienDrawIds?: string[];
  /** True when a report already exists, so changing the selection replaces it. */
  hasExistingReport?: boolean;
  /**
   * `next-report` rolls the period over regardless of the selection, so the dialog explains that
   * instead of warning only when the set changes.
   */
  mode?: 'open' | 'next-report';
}

/** The primary draw drives report sections that are not yet per-draw: the latest one selected. */
const getPrimaryDrawId = (draws: DrawOption[], selectedIds: string[]): string | undefined => {
  const selected = draws.filter(d => selectedIds.includes(d.id));
  if (selected.length === 0) return undefined;
  return selected.reduce((latest, draw) => (Number(draw.applicationNumber ?? 0) >= Number(latest.applicationNumber ?? 0) ? draw : latest))
    .id;
};

const sameSet = (a: string[], b: string[]) => a.length === b.length && a.every(id => b.includes(id));

/**
 * Whether confirming this selection would archive the existing report and start a new one.
 *
 * Shared by the dialog's warning banner and the open flow so they can never disagree.
 *
 * Reports created before multi-draw don't record their draw ids, so `existingDrawIds` is empty and
 * we cannot tell what they cover. Accepting the default single-draw selection for such a report
 * must NOT archive it — the report already covers exactly one draw, we just didn't know which.
 */
export const drawSelectionReplacesReport = (selectedDrawIds: string[], existingDrawIds: string[], hasExistingReport: boolean): boolean => {
  if (!hasExistingReport) return false;
  if (sameSet(selectedDrawIds, existingDrawIds)) return false;
  if (existingDrawIds.length === 0 && selectedDrawIds.length === 1) return false;
  return true;
};

const MagicReportDrawSelectDialog: React.FC<MagicReportDrawSelectDialogProps> = ({
  open,
  onClose,
  draws,
  projectName,
  onConfirm,
  isLoading = false,
  initiallySelectedDrawIds,
  initiallyHiddenCoLienDrawIds,
  hasExistingReport = false,
  mode = 'open',
}) => {
  const visibleDraws = useMemo(() => draws.filter(d => d.isEnabled !== false), [draws]);

  const defaultSelection = useMemo(() => {
    const preselected = (initiallySelectedDrawIds || []).filter(id => visibleDraws.some(d => d.id === id));
    if (preselected.length > 0) {
      if (!MULTI_G702_ENABLED && preselected.length > 1) {
        const primary = getPrimaryDrawId(visibleDraws, preselected);
        return primary ? [primary] : preselected.slice(0, 1);
      }
      return preselected;
    }
    // No existing report (or its draws are gone): default to the latest draw, matching the
    // single-draw behaviour this dialog replaces.
    const latest = getPrimaryDrawId(
      visibleDraws,
      visibleDraws.map(d => d.id),
    );
    return latest ? [latest] : [];
  }, [initiallySelectedDrawIds, visibleDraws]);

  const defaultHidden = useMemo(
    () => (initiallyHiddenCoLienDrawIds || []).filter(id => visibleDraws.some(d => d.id === id)),
    [initiallyHiddenCoLienDrawIds, visibleDraws],
  );

  const [selectedDrawIds, setSelectedDrawIds] = useState<string[]>(defaultSelection);
  const [hiddenCoLienDrawIds, setHiddenCoLienDrawIds] = useState<string[]>(defaultHidden);

  // Re-seed each time the dialog opens so it always reflects the report's current draws.
  React.useEffect(() => {
    if (open) setSelectedDrawIds(defaultSelection);
  }, [open, defaultSelection]);

  React.useEffect(() => {
    if (open) setHiddenCoLienDrawIds(defaultHidden);
  }, [open, defaultHidden]);

  const primaryDrawId = useMemo(() => getPrimaryDrawId(visibleDraws, selectedDrawIds), [visibleDraws, selectedDrawIds]);

  const selectionChanged = useMemo(
    () => drawSelectionReplacesReport(selectedDrawIds, initiallySelectedDrawIds || [], hasExistingReport),
    [selectedDrawIds, initiallySelectedDrawIds, hasExistingReport],
  );

  // With a single draw the answer is forced: hiding it would leave both sections empty.
  const showHiddenColumn = selectedDrawIds.length > 1;

  const effectiveHidden = useMemo(
    () => (showHiddenColumn ? hiddenCoLienDrawIds.filter(id => selectedDrawIds.includes(id)) : []),
    [showHiddenColumn, hiddenCoLienDrawIds, selectedDrawIds],
  );

  const visibleCoLienCount = selectedDrawIds.length - effectiveHidden.length;

  const toggleDraw = (drawId: string) => {
    setSelectedDrawIds(prev => {
      if (prev.includes(drawId)) return prev.filter(id => id !== drawId);
      // Flag off: selecting a draw replaces the selection instead of adding to it.
      return MULTI_G702_ENABLED ? [...prev, drawId] : [drawId];
    });
    // An unselected draw has no sections to hide; drop the flag so re-selecting it starts visible.
    setHiddenCoLienDrawIds(prev => prev.filter(id => id !== drawId));
  };

  const toggleHidden = (drawId: string) => {
    setHiddenCoLienDrawIds(prev => (prev.includes(drawId) ? prev.filter(id => id !== drawId) : [...prev, drawId]));
  };

  const handleConfirm = () => {
    if (selectedDrawIds.length === 0 || visibleCoLienCount === 0) return;
    // Emit in report order (oldest application first) so the wizard's tabs and the printed
    // sections follow the same order everywhere.
    const ordered = visibleDraws.filter(d => selectedDrawIds.includes(d.id)).map(d => d.id);
    onConfirm(
      ordered,
      ordered.filter(id => effectiveHidden.includes(id)),
    );
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth='sm'>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
        <Typography variant='h6'>
          {mode === 'next-report' ? 'Start next report — select draws' : 'Select draws for Magic Report'}
        </Typography>
        <IconButton onClick={onClose} sx={{ marginLeft: 'auto' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {projectName && (
          <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
            {projectName}
          </Typography>
        )}
        {showHiddenColumn && (
          <Stack direction='row' alignItems='flex-end' justifyContent='space-between' sx={{ mb: 0.5 }}>
            <Typography variant='caption' color='text.secondary'>
              Draws
            </Typography>
            <Typography variant='caption' color='text.secondary' sx={{ textAlign: 'right', pr: 0.5 }}>
              CO/Lien Sect&apos;s Hidden
            </Typography>
          </Stack>
        )}
        <FormGroup>
          {visibleDraws.map(draw => {
            const isSelected = selectedDrawIds.includes(draw.id);
            const isHidden = effectiveHidden.includes(draw.id);
            // Every report needs at least one draw carrying the Change Orders and Lien Waivers
            // sections, so the last visible one cannot be switched off.
            const isLastVisible = isSelected && !isHidden && visibleCoLienCount === 1;

            return (
              <Stack key={draw.id} direction='row' alignItems='center' justifyContent='space-between'>
                <FormControlLabel
                  control={<Checkbox checked={isSelected} onChange={() => toggleDraw(draw.id)} value={draw.id} />}
                  label={
                    <Stack direction='row' alignItems='center' spacing={1}>
                      <span>{draw.name}</span>
                      {draw.id === primaryDrawId && selectedDrawIds.length > 1 && (
                        <Chip label='Primary' size='small' color='primary' variant='outlined' sx={{ height: 18, fontSize: '0.65rem' }} />
                      )}
                    </Stack>
                  }
                />
                {showHiddenColumn && (
                  <Tooltip
                    title={
                      !isSelected
                        ? 'Select this draw to choose whether its sections are hidden.'
                        : isLastVisible
                          ? 'At least one draw must keep its Change Orders and Lien Waivers sections.'
                          : isHidden
                            ? 'Hidden: this draw contributes nothing to the Change Orders or Lien Waivers sections, including their comments.'
                            : "Visible: this draw's change orders and lien waivers appear in the report."
                    }
                  >
                    {/* Tooltip needs a non-disabled child to receive hover events. */}
                    <Box component='span'>
                      <Switch
                        size='small'
                        checked={isHidden}
                        disabled={!isSelected || isLastVisible}
                        onChange={() => toggleHidden(draw.id)}
                        inputProps={{ 'aria-label': `Hide Change Orders and Lien Waivers sections for ${draw.name}` }}
                      />
                    </Box>
                  </Tooltip>
                )}
              </Stack>
            );
          })}
        </FormGroup>
        {mode === 'next-report' ? (
          <Alert severity={selectionChanged ? 'warning' : 'info'} sx={{ mt: 2 }}>
            {selectionChanged
              ? "This period's comments move to “last month” for the draws you keep. Draws you remove will not carry forward — their comments stay with the archived report."
              : 'The current report will be archived and its comments will move to “last month”.'}
          </Alert>
        ) : (
          selectionChanged && (
            <Alert severity='warning' sx={{ mt: 2 }}>
              Changing the selected draws will archive the current magic report and start a new one.
            </Alert>
          )
        )}
      </DialogContent>
      <DialogActions>
        <Button variant='outlined' color='secondary' onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant='contained'
          onClick={handleConfirm}
          disabled={selectedDrawIds.length === 0 || visibleCoLienCount === 0 || isLoading}
        >
          {/* Start Next only archives and resets now; it no longer opens the report. */}
          {isLoading ? (mode === 'next-report' ? 'Starting...' : 'Opening...') : 'Continue'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MagicReportDrawSelectDialog;
